import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CircleAlert, CircleCheck, Info } from 'lucide-react';
import {
  useNotifications,
  useNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/usePortal';
import { track } from '../lib/analytics';
import type { PortalNotification } from '../lib/portal';

const PAGE_SIZE = 20;

/**
 * Formats a createdAt ISO string as a relative label (e.g. "2 hours ago",
 * "Yesterday", "3 days ago"). Falls back to a short locale date for older items.
 */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Returns the deep-link target for a notification. step_rejected rows link to
 * the application page, where the step-access contract surfaces the rejection
 * inline on the affected step. (metadata.reasonCode is the curated machine
 * reason code — e.g. INCOMPLETE_DOCUMENTATION — NOT a step key; per-step
 * targeting rides on the step-access snapshot, not the notification.)
 * Other types fall back to their linked entity.
 */
function deepLinkFor(n: PortalNotification): string | null {
  if (n.type === 'step_rejected') return '/application';
  if (n.linkEntityType === 'application') return '/application';
  if (n.linkEntityType === 'document') return '/documents';
  return null;
}

interface NotificationRowProps {
  notification: PortalNotification;
  onRead: (id: string) => void;
}

function NotificationRow({ notification: n, onRead }: NotificationRowProps) {
  const link = deepLinkFor(n);
  const isRejected = n.type === 'step_rejected';
  const hasCuratedReason = isRejected && Boolean(n.metadata);
  const actionLabel = isRejected ? 'Review changes' : 'View update';

  const handleClick = () => {
    if (!n.read) onRead(n.id);
  };

  const inner = (
    <>
      {!n.read && <span className="sr-only">Unread notification. </span>}
      <span className="dotcol">{!n.read && <span className="dot" aria-hidden />}</span>
      <span className={`severity severity-${n.severity}`} aria-hidden>
        {n.severity === 'success' ? (
          <CircleCheck size={18} />
        ) : n.severity === 'warning' || n.severity === 'error' ? (
          <CircleAlert size={18} />
        ) : (
          <Info size={18} />
        )}
      </span>
      <span className="notifbody">
        <span className="notifstate">{isRejected ? 'Changes requested' : n.title}</span>
        {isRejected && <span className="title">{n.title}</span>}
        <span className={`msg${hasCuratedReason ? ' reviewer-feedback' : ''}`}>
          {hasCuratedReason ? n.metadata!.message : n.message}
        </span>
        <span className="notiffooter">
          <time className="time muted" dateTime={n.createdAt}>{relativeTime(n.createdAt)}</time>
          {link && <span className="notifcta">{actionLabel}</span>}
        </span>
      </span>
    </>
  );

  return link ? (
    <Link
      to={link}
      className={`notifrow notiflink severity-row-${n.severity}${n.read ? '' : ' unread'}`}
      onClick={handleClick}
    >
      {inner}
    </Link>
  ) : !n.read ? (
    <button
      type="button"
      aria-label={`Mark notification as read: ${n.title}`}
      className={`notifrow notifbutton severity-row-${n.severity}${n.read ? '' : ' unread'}`}
      onClick={handleClick}
    >
      <span className="sr-only">Mark as read. </span>
      {inner}
    </button>
  ) : (
    <div className={`notifrow severity-row-${n.severity}`}>
      {inner}
    </div>
  );
}

function NotificationSection({
  title,
  notifications,
  onRead,
}: {
  title: string;
  notifications: PortalNotification[];
  onRead: (id: string) => void;
}) {
  if (notifications.length === 0) return null;
  return (
    <section className="notifsection" aria-labelledby={`notifications-${title.replace(/\s/g, '-').toLowerCase()}`}>
      <h2 id={`notifications-${title.replace(/\s/g, '-').toLowerCase()}`}>{title}</h2>
      <div className="card notiflist">
        {notifications.map((n) => (
          <NotificationRow key={n.id} notification={n} onRead={onRead} />
        ))}
      </div>
    </section>
  );
}

/**
 * /notifications — full notification inbox (AP-13).
 *
 * Renders a paged list of all notifications. step_rejected rows use the
 * curated reason-code envelope (metadata.message) as the reviewer feedback
 * when present; legacy rows (null metadata) degrade gracefully to the
 * templated message.
 */
export default function NotificationsPage() {
  const [page, setPage] = useState(1);
  const { notifications, total, loading, error, reload } = useNotifications(page, PAGE_SIZE);
  const notificationCount = useNotificationCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // Inbox impression (Asana 1216270468356070): one rejection_envelope_shown
  // per step_rejected row whose curated envelope is actually rendered
  // (metadata present). Impression semantics: "shown" means the row was
  // rendered in the DOM, NOT that it was viewport-visible. The fired-id set
  // lives for one route mount only, so dedup spans re-renders, page flips and
  // list refetches within that mount — a fresh visit to /notifications
  // re-fires for the same rows by design.
  const firedIds = useRef(new Set<string>());
  useEffect(() => {
    for (const n of notifications) {
      if (n.type !== 'step_rejected' || !n.metadata || firedIds.current.has(n.id)) continue;
      firedIds.current.add(n.id);
      track('rejection_envelope_shown', {
        surface: 'inbox',
        // metadata is truthy-guarded above and reasonCode is non-optional on
        // NotificationReasonMetadata (pairing invariant) — no fallback needed.
        reasonCode: n.metadata.reasonCode,
        notificationId: n.id,
      });
    }
  }, [notifications]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasUnread = notificationCount.unread > 0;
  const actionRequired = notifications.filter((n) => n.requiresAction);
  const recentUpdates = notifications.filter((n) => !n.requiresAction);

  if (loading && notifications.length === 0) {
    return <div className="card">Loading notifications…</div>;
  }

  if (error) {
    return (
      <div className="card" role="alert">
        <p>
          Could not load notifications.{' '}
          <button type="button" className="btn-outline" onClick={reload}>
            Try again
          </button>
        </p>
      </div>
    );
  }

  return (
    <>
      <header className="pagehead notifpagehead">
        <div>
          <h1>
            <Bell size={20} aria-hidden /> Notifications
          </h1>
        </div>
        {hasUnread && (
          <button
            type="button"
            aria-label="Mark all notifications as read"
            className="btn-outline notif-mark-all"
            onClick={() => void markAllRead()}
          >
            Mark all as read
          </button>
        )}
      </header>

      {notifications.length === 0 ? (
        <div className="card">
          <p className="muted">You have no notifications yet.</p>
        </div>
      ) : (
        <>
          <NotificationSection title="Action required" notifications={actionRequired} onRead={markRead} />
          <NotificationSection title="Recent updates" notifications={recentUpdates} onRead={markRead} />
        </>
      )}

      {totalPages > 1 && (
        <div className="notifpager">
          <button
            type="button"
            className="btn-outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="muted">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="btn-outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
